import {
  Badge,
  NumericMarketData,
  OptionData,
  OutOfTheMoney,
  StrategyType,
  Time,
} from "../types";
import { MarketData } from "../types/MarketData";

// Definition of The variables to calculate values of Income Strategies
export const incomeGenerations: Partial<
  Record<keyof MarketData, { percentage: number; multiplier: number }>
> = {
  "Calls - at the money 1 month": { percentage: 4, multiplier: 1 },
  "Calls - at the money 3 month": { percentage: 10, multiplier: 1 },
  "Calls - at the money 6 month": { percentage: 15, multiplier: 1 },
  "Calls - at the money 1 Year": { percentage: 20, multiplier: 1 },
  "Calls - 5% out of the money 1 month": { percentage: 3, multiplier: 1.5 },
  "Calls - 5% out of the money 3 month": { percentage: 7, multiplier: 2 },
  "Calls - 5% out of the money 6 month": { percentage: 10, multiplier: 1.75 },
  "Calls - 5% out of the money 1 Year": { percentage: 15, multiplier: 1.5 },
  "Calls - 10% out of the money 1 month": { percentage: 2, multiplier: 1.5 },
  "Calls - 10% out of the money 3 month": { percentage: 5, multiplier: 2 },
  "Calls - 10% out of the money 6 month": { percentage: 7, multiplier: 2.5 },
  "Calls - 10% out of the money 1 Year": { percentage: 10, multiplier: 2 },
  "Calls - 15% out of the money 1 month": { percentage: 2, multiplier: 1.5 },
  "Calls - 15% out of the money 3 month": { percentage: 4, multiplier: 2.5 },
  "Calls - 15% out of the money 6 month": { percentage: 6, multiplier: 2.5 },
  "Calls - 15% out of the money 1 Year": { percentage: 10, multiplier: 3 },
  "Puts - at the money 1 month": { percentage: 4, multiplier: 1 },
  "Puts - at the money 3 month": { percentage: 10, multiplier: 1 },
  "Puts - at the money 6 month": { percentage: 15, multiplier: 1 },
  "Puts - at the money 1 Year": { percentage: 20, multiplier: 1 },
  "Puts - 5% out of the money 1 month": { percentage: 3, multiplier: 1 },
  "Puts - 5% out of the money 3 month": { percentage: 7, multiplier: 1 },
  "Puts - 5% out of the money 6 month": { percentage: 10, multiplier: 1.5 },
  "Puts - 5% out of the money 1 Year": { percentage: 15, multiplier: 1 },
  "Puts - 10% out of the money 1 month": { percentage: 2, multiplier: 1 },
  "Puts - 10% out of the money 3 month": { percentage: 5, multiplier: 1 },
  "Puts - 10% out of the money 6 month": { percentage: 7, multiplier: 2 },
  "Puts - 10% out of the money 1 Year": { percentage: 10, multiplier: 1.5 },
  "Puts - 15% out of the money 1 month": { percentage: 2, multiplier: 1 },
  "Puts - 15% out of the money 3 month": { percentage: 4, multiplier: 1 },
  "Puts - 15% out of the money 6 month": { percentage: 6, multiplier: 1.5 },
  "Puts - 15% out of the money 1 Year": { percentage: 10, multiplier: 1.5 },
};

export const incomeGenerationsList: Array<{
  key: keyof MarketData;
  percentage: number;
  multiplier: number;
}> = Object.entries(incomeGenerations).map(([key, value]) => ({
  key: key as keyof MarketData,
  percentage: value.percentage,
  multiplier: value.multiplier,
}));

const optionNameDictionary: Partial<Record<keyof MarketData, string>> = {
  "Calls - at the money 1 month": "Calls 1m 100%",
  "Calls - at the money 3 month": "Calls 3m 100%",
  "Calls - at the money 6 month": "Calls 6m 100%",
  "Calls - at the money 1 Year": "Calls 1y 100%",
  "Calls - 5% out of the money 1 month": "Calls 1m 105%",
  "Calls - 5% out of the money 3 month": "Calls 3m 105%",
  "Calls - 5% out of the money 6 month": "Calls 6m 105%",
  "Calls - 5% out of the money 1 Year": "Calls 1y 105%",
  "Calls - 10% out of the money 1 month": "Calls 1m 110%",
  "Calls - 10% out of the money 3 month": "Calls 3m 110%",
  "Calls - 10% out of the money 6 month": "Calls 6m 110%",
  "Calls - 10% out of the money 1 Year": "Calls 1y 110%",
  "Calls - 15% out of the money 1 month": "Calls 1m 115%",
  "Calls - 15% out of the money 3 month": "Calls 3m 115%",
  "Calls - 15% out of the money 6 month": "Calls 6m 115%",
  "Calls - 15% out of the money 1 Year": "Calls 1y 115%",
  "Puts - at the money 1 month": "Puts 1m 100%",
  "Puts - at the money 3 month": "Puts 3m 100%",
  "Puts - at the money 6 month": "Puts 6m 100%",
  "Puts - at the money 1 Year": "Puts 1y 100%",
  "Puts - 5% out of the money 1 month": "Puts 1m 95%",
  "Puts - 5% out of the money 3 month": "Puts 3m 95%",
  "Puts - 5% out of the money 6 month": "Puts 6m 95%",
  "Puts - 5% out of the money 1 Year": "Puts 1y 95%",
  "Puts - 10% out of the money 1 month": "Puts 1m 90%",
  "Puts - 10% out of the money 3 month": "Puts 3m 90%",
  "Puts - 10% out of the money 6 month": "Puts 6m 90%",
  "Puts - 10% out of the money 1 Year": "Puts 1y 90%",
  "Puts - 15% out of the money 1 month": "Puts 1m 85%",
  "Puts - 15% out of the money 3 month": "Puts 3m 85%",
  "Puts - 15% out of the money 6 month": "Puts 6m 85%",
  "Puts - 15% out of the money 1 Year": "Puts 1y 85%",
};

const formatTo1Decimal = (value: number | null | undefined) => {
  return value?.toFixed(1) ?? "N/A";
};

// Helper function to parse option name and extract details
const parseOptionName = (
  optionName: keyof MarketData
): {
  optionType: "call" | "put";
  outOfTheMoney: OutOfTheMoney;
  time: Time;
} | null => {
  const nameStr = optionName.toString();
  const isCall = nameStr.startsWith("Calls");
  const isPut = nameStr.startsWith("Puts");

  if (!isCall && !isPut) return null;

  // Extract out of the money percentage
  let outOfTheMoney: OutOfTheMoney;
  if (nameStr.includes("at the money")) {
    outOfTheMoney = OutOfTheMoney.AT_THE_MONEY;
  } else if (nameStr.includes("15% out of the money")) {
    outOfTheMoney = OutOfTheMoney["15_OUT_OF_THE_MONEY"];
  } else if (nameStr.includes("5% out of the money")) {
    outOfTheMoney = OutOfTheMoney["5_OUT_OF_THE_MONEY"];
  } else if (nameStr.includes("10% out of the money")) {
    outOfTheMoney = OutOfTheMoney["10_OUT_OF_THE_MONEY"];
  } else {
    return null;
  }

  // Extract time
  let time: Time;
  if (nameStr.includes("1 month")) {
    time = Time.ONE_MONTH;
  } else if (nameStr.includes("3 month")) {
    time = Time.THREE_MONTHS;
  } else if (nameStr.includes("6 month")) {
    time = Time.SIX_MONTHS;
  } else {
    return null;
  }

  return {
    optionType: isCall ? "call" : "put",
    outOfTheMoney,
    time,
  };
};

// Helper function to create strategy-like object
const createStrategyFromOption = (
  optionName: keyof MarketData,
  friendlyName: string,
  calculatedValue: number,
  marketData: NumericMarketData
) => {
  const parsed = parseOptionName(optionName);
  if (!parsed) return null;

  const { optionType, outOfTheMoney, time } = parsed;
  const optionData: OptionData = {
    strategyType:
      optionType === "call"
        ? StrategyType.SyntheticYield
        : StrategyType.IncomeShortPut,
    operations: [
      {
        operationType: "short" as const,
        optionType: optionType,
        outOfTheMoney: outOfTheMoney,
      },
    ],
    stockQuantity: optionType === "call" ? 100 : 0,
    time: time,
  };

  const id = friendlyName.toLowerCase().replace(/\s+/g, "-").replace(/%/g, "");
  const badge = Badge.Income;
  const subtitle =
    optionType === "call"
      ? "Generate income on an existing position with a short call"
      : "Generate income on an existing position with a short put";

  const optionValue = marketData[optionName];
  const otmStr =
    outOfTheMoney === OutOfTheMoney.AT_THE_MONEY
      ? "at the money"
      : outOfTheMoney.replace("AT THE MONEY", "").toLowerCase().trim();
  const timeStr =
    time === Time.ONE_MONTH ? "1m" : time === Time.THREE_MONTHS ? "3m" : "6m";
  const description =
    typeof optionValue === "number"
      ? `A ${timeStr} ${otmStr} ${optionType} generates ${formatTo1Decimal(
          optionValue
        )}%`
      : "N/A";

  return {
    id,
    label: friendlyName,
    badge,
    optionData: optionData satisfies OptionData,
    subtitle,
    description,
    calculatedValue,
    value: calculatedValue > 0,
    valid: (instrument: NumericMarketData) => {
      const optionValue = instrument[optionName];
      return (
        optionValue != null &&
        optionValue !== 0 &&
        typeof optionValue === "number"
      );
    },
  };
};

type IncomeStrategy = {
  id: string;
  label: string;
  badge: Badge;
  optionData: OptionData;
  subtitle: string;
  description: string;
  value: boolean;
  valid: (instrument: NumericMarketData) => boolean;
};

export const getIncomeGeneration = (
  marketData: NumericMarketData
): {
  topCalls: IncomeStrategy[];
  topPuts: IncomeStrategy[];
  allCalls: IncomeStrategy[];
  allPuts: IncomeStrategy[];
} => {
  const results = incomeGenerationsList
    .map((item) => {
      const x = marketData[item.key];

      // Skip null values and ensure it's a number
      if (x === null || x === undefined || typeof x !== "number") {
        return null;
      }

      // Calculate (x - percentage) * multiplier and round to 1 decimal place
      const calculatedValue =
        Math.round((x - item.percentage) * item.multiplier * 10) / 10;

      const friendlyName =
        optionNameDictionary[item.key] || item.key.toString();

      const strategy = createStrategyFromOption(
        item.key,
        friendlyName,
        calculatedValue,
        marketData
      );

      if (!strategy) return null;

      return {
        ...strategy,
        calculatedValue, // Keep for sorting
      };
    })
    .filter((item) => item !== null)
    .sort((a, b) => b!.calculatedValue - a!.calculatedValue); // Sort descending

  // Separate calls and puts
  const calls = results.filter(
    (item) => item!.optionData.operations[0].optionType === "call"
  );
  const puts = results.filter(
    (item) => item!.optionData.operations[0].optionType === "put"
  );

  // Get top 3 of each and remove calculatedValue
  const stripCalculatedValue = (items: typeof results) =>
    items.map((item) => {
      const { calculatedValue, ...strategy } = item!;
      return strategy;
    });

  const allCalls = stripCalculatedValue(calls);
  const allPuts = stripCalculatedValue(puts);

  return {
    topCalls: allCalls.slice(0, 3),
    topPuts: allPuts.slice(0, 3),
    allCalls,
    allPuts,
  };
};
